'use client';

import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { LOCAL_STORAGE_DEVICE_ID_KEY } from '@module/mdrt-org/shared/utils/data/constants/local-storage-keys';
import { deleteWebPushSubscription } from '@module/mdrt-org/shared/utils/data/delete-web-push-subscription';
import { postWebPushSubscription } from '@module/mdrt-org/shared/utils/data/post-web-push-subscription';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import {
  extractSubscriptionKeys,
  isPushSupported,
} from '@module/mdrt-org/shared/utils/helpers/web-push-subscription';

import { registerPeriodicBadgeSync } from './register-periodic-badge-sync';

export const ServiceWorkerInit = () => {
  const { accessToken, authenticatedPersonId } = useUserContext();
  const hasSynced = useRef(false);

  const { mutate } = useMutation({
    mutationFn: postWebPushSubscription,
    mutationKey: [AccountSettingsMutationKey.PATCH_PUSH_NOTIFICATION_SUBSCRIPTION],
  });

  const { mutateAsync: deleteSubscriptionAsync } = useMutation({
    mutationFn: deleteWebPushSubscription,
    mutationKey: [AccountSettingsMutationKey.DELETE_PUSH_NOTIFICATION_SUBSCRIPTION],
  });

  useEffect(() => {
    if (hasSynced.current || !accessToken) return;

    if (!isPushSupported()) {
      hasSynced.current = true;
      return;
    }

    const syncSubscription = async () => {
      if (Notification.permission === 'denied') {
        const storedDeviceId = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY);

        if (storedDeviceId) {
          try {
            await deleteSubscriptionAsync({
              accessToken,
              deviceId: storedDeviceId,
              personId: authenticatedPersonId,
            });
            localStorage.removeItem(LOCAL_STORAGE_DEVICE_ID_KEY);
          } catch {
            // best-effort cleanup — ignore failures
          }
        }

        return;
      }

      const registration = await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) return;

      const existingDeviceId = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY);
      const deviceId = existingDeviceId ?? crypto.randomUUID();

      if (!existingDeviceId) {
        localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, deviceId);
      }

      const { auth, endpoint, p256dh } = extractSubscriptionKeys(subscription);

      mutate({
        accessToken,
        auth,
        deviceId,
        endpoint,
        expirationTime: subscription.expirationTime,
        p256dh,
        personId: authenticatedPersonId,
        userAgent: navigator.userAgent,
      });
    };

    hasSynced.current = true;
    syncSubscription();
  }, [accessToken, authenticatedPersonId, deleteSubscriptionAsync, mutate]);

  // Periodic Background Sync registration for the PWA badge (independent of push), so
  // the browser refreshes the badge while the app is closed. Unregistered on logout by
  // `cleanupAppBadgeOnLogout`.
  useEffect(() => {
    if (!accessToken || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const syncPeriodicBadge = async () => {
      const registration = await navigator.serviceWorker.ready;

      await registerPeriodicBadgeSync(registration);
    };

    void syncPeriodicBadge();
  }, [accessToken]);

  return null;
};
